import { createContext, PropsWithChildren, ReactNode, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useRequiredContext } from '@src/utils/hooks/useRequiredContext';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { clientsKey, paramsToClients } from './Filters/activity-feed-filters-utils';

interface OracleCidScopeParams {
  cid?: string;
  cids?: string;
}

interface OracleCidScope {
  cid: string;
  selectedCids: string[];
  params: OracleCidScopeParams;
}

// The route cid wins; otherwise send the selected clients as one comma-separated string.
const buildScopeParams = (cid: string, selectedCids: string[]): OracleCidScopeParams => {
  if (cid) return { cid };
  if (selectedCids.length > 0) return { cids: selectedCids.join(',') };
  return {};
};

export const useUrlOracleCidScope = (): OracleCidScope => {
  const { cid } = useCompanyInfo();
  const [searchParams] = useSearchParams();
  // Joined so the memo keys on the client set, not on every URL change (e.g. `selected=`).
  const selectedCidsKey = clientsKey(paramsToClients(searchParams));
  return useMemo(() => {
    const selectedCids = selectedCidsKey ? selectedCidsKey.split(',') : [];
    return { cid, selectedCids, params: buildScopeParams(cid, selectedCids) };
  }, [cid, selectedCidsKey]);
};

const OracleCidScopeContext = createContext<OracleCidScope | null>(null);

// Consumers re-render when the client set changes, not on every search-param change.
export const OracleCidScopeProvider = ({ children }: PropsWithChildren): ReactNode => {
  const scope = useUrlOracleCidScope();
  return <OracleCidScopeContext.Provider value={scope}>{children}</OracleCidScopeContext.Provider>;
};

export const useOracleCidScope = (): OracleCidScope =>
  useRequiredContext(OracleCidScopeContext, 'useOracleCidScope', 'OracleCidScopeProvider');
